import MaskedView from "@react-native-masked-view/masked-view";
import { LinearGradient } from "expo-linear-gradient";
import React, { ReactElement, memo } from "react";
import { StyleProp, StyleSheet, ViewStyle } from "react-native";
import Reanimated from "react-native-reanimated";

import { Box } from "@/components/atoms/Box";

// Web bundle (.native.tsx serves native): the shimmer container is always the
// Reanimated view that consumes the animated style.
const AnimatedView = Reanimated.View;

/* design-system: dynamic-style waiver — data-driven value (measured layout dimensions for MaskedView) */
const maskedViewStyle = (width: number, height: number) =>
  ({ width, height }) as const;

/**
 * Props for the SkeletonPlaceholderView component
 */
interface SkeletonProps {
  children: ReactElement;
  layoutWidth: number;
  layoutHeight: number;

  animStyle: StyleProp<ViewStyle>;
  /**
   * Whether dark theme is active
   */
  isDark: boolean;
}

/**
 * View component that renders skeleton placeholder with animated shimmer effect
 * @param root0 - Component props
 * @param root0.children - React element to be masked with skeleton overlay
 * @param root0.layoutWidth - Width of the skeleton placeholder in pixels
 * @param root0.layoutHeight - Height of the skeleton placeholder in pixels
 * @param root0.animStyle - Animated style object for the shimmer effect
 * @param root0.isDark - Boolean flag indicating if dark mode is active
 */
const SkeletonPlaceholderView: React.FC<SkeletonProps> = ({
  children,
  layoutWidth,
  layoutHeight,
  animStyle,
  isDark,
}) => (
  <MaskedView
    maskElement={children}
    // eslint-disable-next-line no-restricted-syntax -- third-party MaskedView (no atom); measured runtime layout dims
    style={maskedViewStyle(layoutWidth, layoutHeight)}
  >
    <Box
      className={`grow overflow-hidden opacity-60 ${isDark ? "bg-surface-strong" : "bg-surface-muted"}`}
    />
    {/* eslint-disable-next-line no-restricted-syntax -- Reanimated.View consuming animated shimmer style */}
    <AnimatedView style={[StyleSheet.absoluteFill, animStyle]}>
      <MaskedView
        // eslint-disable-next-line no-restricted-syntax -- third-party MaskedView (no atom); absolute-fill overlay
        style={StyleSheet.absoluteFill}
        maskElement={
          <LinearGradient
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            // eslint-disable-next-line no-restricted-syntax -- third-party expo LinearGradient (no atom); absolute-fill mask
            style={StyleSheet.absoluteFill}
            colors={["transparent", isDark ? "black" : "white", "transparent"]}
          />
        }
      >
        <Box
          className={`absolute inset-0 opacity-40 ${isDark ? "bg-surface-muted" : "bg-surface-raised"}`}
        />
      </MaskedView>
    </AnimatedView>
  </MaskedView>
);

SkeletonPlaceholderView.displayName = "SkeletonPlaceholderView";

export default memo(SkeletonPlaceholderView);
